import axios from 'axios';
import ResourceAPI, { Resource, SearchParams } from 'api/ResourceAPI';
import { secureTokenStorage } from 'utils/secureTokenStorage';

export interface QueryStatusResult {
  log_id: number;
  thread_id: string;
  status: 'pending' | 'complete' | 'failed';
  query: string;
  sql_query: string;
  response: string;
  poll_after_ms: number | null;
}

// Mirrors the Tenant/X-Tenant resolution ResourceAPI's interceptor applies
// (from the 'active_customer' entry in localStorage), so calls that can't go
// through ResourceAPI still scope RoleAuthorizer to the active tenant instead
// of falling back to its "highest role across all tenants" mode.
function getTenantHeaders(): Record<string, string> {
  const customer = localStorage.getItem('active_customer');
  if (!customer) return {};
  try {
    const parsed = JSON.parse(customer);
    if (parsed?.slug) {
      return { Tenant: parsed.slug, 'X-Tenant': parsed.slug };
    }
  } catch {
    // Not valid JSON - no tenant header to add.
  }
  return {};
}

export default class AIApiService {
  public static instance: AIApiService;

  public static baseUrl = process.env.REACT_APP_AI_API_URL;

  public static baseUrlCreate = 'ai-create-new-thread' as Resource;

  public static submitFeedbackUrl = 'ai-submit-feedback' as Resource;

  public static get Instance() {
    // eslint-disable-next-line no-return-assign
    return AIApiService.instance || (AIApiService.instance = new AIApiService());
  }

  // POST /ed/v1/ai-query-engine (ai-query-submit). Fast, synchronous call
  // that returns { log_id, thread_id, status: 'pending' } (202) right away -
  // the actual query runs on an SQS-driven worker, so this never carries the
  // answer itself. Poll GetQueryStatus(log_id) for the result.
  public static async SubmitQuery(data: any) {
    try {
      const idToken = secureTokenStorage.get()?.id_token ?? '';
      const res = await axios.post(`${this.baseUrl}`, data, {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${idToken}`,
          ...getTenantHeaders(),
        },
      });

      return res.data;
    } catch (error: unknown) {
      return null;
    }
  }

  // GET /ed/v1/ai-query-engine/{log_id} (ai-query-status). Poll target for
  // the log_id returned by SubmitQuery.
  public static async GetQueryStatus(logId: string | number): Promise<QueryStatusResult | null> {
    try {
      const idToken = secureTokenStorage.get()?.id_token ?? '';
      const res = await axios.get(`${this.baseUrl}/${logId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${idToken}`,
          ...getTenantHeaders(),
        },
      });

      return res.data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async createThreadAI(payload: any) {
    try {
      const { data } = await ResourceAPI.post(this.baseUrlCreate, payload);
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async submitFeedback(payload: any) {
    try {
      const { data } = await ResourceAPI.post(this.submitFeedbackUrl, payload);
      return data;
    } catch (error: unknown) {
      return null;
    }
  }
}
